import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import { T, FONT_MONO, INDICATOR, INDICATOR_TEXT } from '../constants/tokens';

/** Chips shown before the rest collapse into "+N more", as the design has it. */
const MAX_CHIPS = 4;

const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    border: 0,
    borderRadius: 9999,
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    cursor: 'pointer',
    '&:hover': { filter: 'brightness(0.95)' },
    '&:focus-visible': { outline: '2px solid ' + T.focus, outlineOffset: 2 }
  }
});

const TONES = {
  bad: { dot: INDICATOR.error, fg: INDICATOR_TEXT.error, bg: 'rgba(205,66,57,0.12)' },
  warn: { dot: INDICATOR.warning, fg: INDICATOR_TEXT.warning, bg: 'rgba(247,165,1,0.18)' }
};

/**
 * A device group's health at a glance: a bar split into working (green), low
 * battery (yellow) and offline (red), and a chip for each device that is not
 * working, by name. Choosing a chip opens that device.
 *
 * The chips are buttons, so whatever contains this must not itself be a
 * button: see StatCard and DeviceHealth.
 *
 * @param {{ ok: number, warn?: number, bad: number }} counts
 * @param {Array<{ key: string, label: string, tone: 'bad'|'warn', onOpen: function }>} issues
 *   worst first
 */
export default function DeviceHealthBar({ counts, issues = [], onMore = null }) {
  const classes = useStyles();

  const segments = [
    { key: 'ok', n: counts.ok, color: INDICATOR.success },
    { key: 'warn', n: counts.warn ?? 0, color: INDICATOR.warning },
    { key: 'bad', n: counts.bad, color: INDICATOR.error }
  ].filter((s) => s.n > 0);

  const shown = issues.slice(0, MAX_CHIPS);
  const more = issues.length - shown.length;
  const total = counts.ok + (counts.warn ?? 0) + counts.bad;

  return (
    <>
      <div
        role='img'
        aria-label={total === 0
          ? 'No devices'
          : `${counts.ok} working, ${counts.warn ?? 0} low battery, ${counts.bad} offline`}
        style={{ display: 'flex', gap: 2, height: 8, borderRadius: 9999, overflow: 'hidden', marginTop: 12, background: T.hairlineSoft }}
      >
        {segments.map((s) => (
          <div key={s.key} style={{ height: '100%', flex: s.n + ' 0 0', background: s.color }} />
        ))}
      </div>

      {issues.length > 0 &&
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {shown.map((issue) => {
            const tone = TONES[issue.tone] ?? TONES.bad;
            return (
              <button
                key={issue.key}
                type='button'
                className={classes.chip}
                style={{ background: tone.bg, color: tone.fg }}
                onClick={(e) => {
                  // The card behind the chip has its own action.
                  e.stopPropagation();
                  issue.onOpen();
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 9999, flex: 'none', background: tone.dot }} />
                {issue.label}
              </button>
            );
          })}
          {more > 0 &&
            <button
              type='button'
              className={classes.chip}
              style={{ background: T.hairlineSoft, color: T.body }}
              onClick={(e) => {
                e.stopPropagation();
                if (onMore) onMore();
              }}
            >
              +{more} more
            </button>}
        </div>}
    </>
  );
}

DeviceHealthBar.propTypes = {
  counts: PropTypes.shape({
    ok: PropTypes.number.isRequired,
    warn: PropTypes.number,
    bad: PropTypes.number.isRequired
  }).isRequired,
  issues: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    tone: PropTypes.oneOf(['bad', 'warn']).isRequired,
    onOpen: PropTypes.func.isRequired
  })),
  onMore: PropTypes.func
};
